import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { assessmentService } from '../../../services/assessmentService';
import { useNavigate } from 'react-router-dom';

const AIReadinessAssessment = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const assessmentDimensions = [
    {
      id: 'data_infrastructure',
      title: 'Data Infrastructure',
      description: 'Evaluate your data foundation and accessibility',
      questions: [
        {
          id: 'data_centralization',
          question: 'How centralized is your organization\'s data?',
          options: [
            { value: 1, label: 'Highly fragmented across multiple systems with no integration' },
            { value: 2, label: 'Some integration but significant silos remain' },
            { value: 3, label: 'Mostly centralized with minor exceptions' },
            { value: 4, label: 'Fully centralized data warehouse or data lake' }
          ]
        },
        {
          id: 'data_quality',
          question: 'What is the quality of your data?',
          options: [
            { value: 1, label: 'Significant quality issues, inconsistent formats' },
            { value: 2, label: 'Some quality issues, manual cleaning required' },
            { value: 3, label: 'Generally good quality with automated validation' },
            { value: 4, label: 'High quality with comprehensive data governance' }
          ]
        },
        {
          id: 'data_volume',
          question: 'What volume of data do you have available for AI?',
          options: [
            { value: 1, label: 'Limited data, insufficient for meaningful AI models' },
            { value: 2, label: 'Moderate data volume, suitable for basic AI applications' },
            { value: 3, label: 'Substantial data volume across multiple domains' },
            { value: 4, label: 'Extensive historical and real-time data' }
          ]
        }
      ]
    },
    {
      id: 'governance',
      title: 'AI Governance & Ethics',
      description: 'Assess your governance frameworks and ethical considerations',
      questions: [
        {
          id: 'ai_policy',
          question: 'Does your organization have AI governance policies?',
          options: [
            { value: 1, label: 'No formal AI policies or guidelines' },
            { value: 2, label: 'Basic guidelines in development' },
            { value: 3, label: 'Established policies with some enforcement' },
            { value: 4, label: 'Comprehensive AI governance framework with active oversight' }
          ]
        },
        {
          id: 'explainability',
          question: 'How important is AI explainability in your organization?',
          options: [
            { value: 1, label: 'Not considered or understood' },
            { value: 2, label: 'Recognized as important but not implemented' },
            { value: 3, label: 'Implemented for high-risk decisions' },
            { value: 4, label: 'Mandatory for all AI systems with audit trails' }
          ]
        },
        {
          id: 'compliance',
          question: 'How do you handle AI regulatory compliance?',
          options: [
            { value: 1, label: 'No compliance framework in place' },
            { value: 2, label: 'Aware of requirements but not fully compliant' },
            { value: 3, label: 'Compliant with major regulations (GDPR, AI Act)' },
            { value: 4, label: 'Proactive compliance with dedicated oversight team' }
          ]
        }
      ]
    },
    {
      id: 'team_capabilities',
      title: 'Team Capabilities',
      description: 'Evaluate your team\'s AI skills and expertise',
      questions: [
        {
          id: 'ai_expertise',
          question: 'What level of AI expertise exists in your organization?',
          options: [
            { value: 1, label: 'No dedicated AI/ML expertise' },
            { value: 2, label: 'Some team members with basic AI knowledge' },
            { value: 3, label: 'Dedicated AI team with practical experience' },
            { value: 4, label: 'Advanced AI team with proven track record' }
          ]
        },
        {
          id: 'training',
          question: 'How does your organization approach AI training?',
          options: [
            { value: 1, label: 'No AI training programs' },
            { value: 2, label: 'Ad-hoc training when needed' },
            { value: 3, label: 'Regular training programs for key staff' },
            { value: 4, label: 'Comprehensive AI literacy program across organization' }
          ]
        },
        {
          id: 'collaboration',
          question: 'How do technical and business teams collaborate on AI?',
          options: [
            { value: 1, label: 'Minimal collaboration, siloed teams' },
            { value: 2, label: 'Occasional collaboration on specific projects' },
            { value: 3, label: 'Regular collaboration with defined processes' },
            { value: 4, label: 'Integrated teams with shared AI objectives' }
          ]
        }
      ]
    },
    {
      id: 'strategic_alignment',
      title: 'Strategic Alignment',
      description: 'Assess AI alignment with business strategy',
      questions: [
        {
          id: 'ai_strategy',
          question: 'Does your organization have an AI strategy?',
          options: [
            { value: 1, label: 'No AI strategy or roadmap' },
            { value: 2, label: 'Informal AI initiatives without clear strategy' },
            { value: 3, label: 'Documented AI strategy aligned with business goals' },
            { value: 4, label: 'Comprehensive AI strategy with executive sponsorship' }
          ]
        },
        {
          id: 'use_cases',
          question: 'How are AI use cases identified and prioritized?',
          options: [
            { value: 1, label: 'No systematic approach to identifying use cases' },
            { value: 2, label: 'Opportunistic identification without prioritization' },
            { value: 3, label: 'Structured process with business value assessment' },
            { value: 4, label: 'Strategic portfolio management with ROI tracking' }
          ]
        },
        {
          id: 'investment',
          question: 'What is your organization\'s AI investment approach?',
          options: [
            { value: 1, label: 'No dedicated AI budget' },
            { value: 2, label: 'Limited budget for experimental projects' },
            { value: 3, label: 'Dedicated AI budget with clear allocation' },
            { value: 4, label: 'Strategic AI investment with multi-year commitment' }
          ]
        }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep < assessmentDimensions?.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateResults();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateResults = () => {
    const dimensionScores = {};
    let totalScore = 0;
    let totalQuestions = 0;

    assessmentDimensions?.forEach(dimension => {
      let dimensionTotal = 0;
      let dimensionCount = 0;

      dimension?.questions?.forEach(question => {
        const answer = answers?.[question?.id];
        if (answer) {
          dimensionTotal += answer;
          dimensionCount++;
        }
      });

      const dimensionScore = dimensionCount > 0 ? (dimensionTotal / (dimensionCount * 4)) * 100 : 0;
      dimensionScores[dimension?.id] = {
        score: Math.round(dimensionScore),
        title: dimension?.title
      };

      totalScore += dimensionTotal;
      totalQuestions += dimensionCount;
    });

    const overallScore = totalQuestions > 0 ? (totalScore / (totalQuestions * 4)) * 100 : 0;

    let maturityLevel = '';
    let recommendation = '';
    let color = '';
    let nextSteps = [];

    if (overallScore >= 75) {
      maturityLevel = 'AI-Ready';
      recommendation = 'Your organization demonstrates strong AI readiness across all dimensions. Focus on scaling existing capabilities and exploring advanced AI applications.';
      color = 'text-green-600';
      nextSteps = [
        'Deploy production AI systems with confidence',
        'Establish centers of excellence for AI innovation',
        'Explore cutting-edge AI technologies (LLMs, generative AI)',
        'Build AI partnerships and ecosystem'
      ];
    } else if (overallScore >= 50) {
      maturityLevel = 'AI-Capable';
      recommendation = 'Your organization has solid foundations for AI adoption. Strengthen governance frameworks and expand team capabilities to accelerate AI initiatives.';
      color = 'text-blue-600';
      nextSteps = [
        'Pilot AI projects in controlled environments',
        'Establish AI governance and ethics framework',
        'Invest in team training and capability building',
        'Develop AI strategy aligned with business goals'
      ];
    } else if (overallScore >= 25) {
      maturityLevel = 'AI-Emerging';
      recommendation = 'Your organization is beginning the AI journey. Focus on building data infrastructure, establishing governance, and developing foundational AI literacy.';
      color = 'text-yellow-600';
      nextSteps = [
        'Assess and improve data quality and accessibility',
        'Build foundational AI literacy across organization',
        'Define AI governance policies and ethical guidelines',
        'Identify high-value AI use cases'
      ];
    } else {
      maturityLevel = 'AI-Foundational';
      recommendation = 'Your organization is at the early stages of AI readiness. Prioritize data infrastructure, governance frameworks, and organizational awareness before pursuing AI initiatives.';
      color = 'text-orange-600';
      nextSteps = [
        'Establish data governance and quality standards',
        'Build executive awareness of AI opportunities and risks',
        'Assess current data infrastructure capabilities',
        'Develop AI strategy and roadmap'
      ];
    }

    setResult({
      overallScore: Math.round(overallScore),
      maturityLevel,
      recommendation,
      color,
      nextSteps,
      dimensionScores
    });
    setShowResults(true);
  };

  const handleSaveResults = async () => {
    if (!email) {
      alert('Please enter your email to save results');
      return;
    }

    setSaving(true);
    try {
      // Get or create session ID for conversion tracking
      let sessionId = sessionStorage.getItem('conversion_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
        sessionStorage.setItem('conversion_session_id', sessionId);
      }

      const savedAssessment = await assessmentService?.saveAssessment(
        'ai_readiness',
        'AI Readiness Assessment',
        answers,
        result
      );

      // Track assessment completion in Google Analytics
      const { trackAssessmentCompletion } = await import('../../../utils/analyticsEvents');
      trackAssessmentCompletion(
        'ai_readiness',
        result?.overallScore,
        result?.readinessLevel
      );

      // Track assessment completion event in Supabase
      const { analyticsService } = await import('../../../services/analyticsService');
      await analyticsService?.trackConversionEvent({
        sessionId,
        eventType: 'assessment_complete',
        eventCategory: 'Assessment',
        eventData: {
          assessmentType: 'ai_readiness',
          score: result?.overallScore,
          maturityLevel: result?.readinessLevel
        }
      });

      // Create conversion funnel record
      await analyticsService?.upsertConversionFunnel({
        sessionId,
        assessmentId: savedAssessment?.id,
        assessmentType: 'ai_readiness',
        assessmentScore: result?.overallScore,
        maturityLevel: result?.readinessLevel,
        conversionStatus: 'assessment_only'
      });

      // Store assessment data in session for potential booking conversion tracking
      sessionStorage.setItem('assessment_data', JSON.stringify({
        assessmentType: 'ai_readiness',
        score: result?.overallScore,
        maturityLevel: result?.readinessLevel,
        completedAt: new Date()?.toISOString()
      }));
      sessionStorage.setItem('booking_source', 'assessment');

      alert('Assessment results saved successfully! You can view them in your dashboard.');
      navigate('/client-portal-dashboard-personalized-analytics-hub');
    } catch (error) {
      console.error('Error saving assessment:', error);
      alert('Failed to save assessment. Please sign in to save your results.');
    } finally {
      setSaving(false);
    }
  };

  const currentDimension = assessmentDimensions?.[currentStep];
  const progress = ((currentStep + 1) / assessmentDimensions?.length) * 100;
  const isCurrentStepComplete = currentDimension?.questions?.every(
    q => answers?.[q?.id] !== undefined
  );

  if (showResults && result) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-full mb-4">
            <Icon name="Award" size={40} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-primary mb-2">AI Readiness Assessment Results</h2>
          <p className="text-muted-foreground">Comprehensive evaluation of your organization's AI maturity</p>
        </div>

        {/* Overall Score */}
        <div className="flex items-center justify-center mb-8">
          <div className="relative inline-flex items-center justify-center w-48 h-48">
            <svg className="transform -rotate-90 w-48 h-48">
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="#e5e7eb"
                strokeWidth="12"
                fill="none"
              />
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="#1FB5A5"
                strokeWidth="12"
                fill="none"
                strokeDasharray={`${result?.overallScore * 5.53} 553`}
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute text-center">
              <div className="text-5xl font-bold text-primary">{result?.overallScore}</div>
              <div className="text-sm text-muted-foreground">Overall Score</div>
            </div>
          </div>
        </div>

        <div className="text-center mb-8">
          <div className={`text-3xl font-bold mb-3 ${result?.color}`}>{result?.maturityLevel}</div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">{result?.recommendation}</p>
        </div>

        {/* Dimension Scores */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {Object?.entries(result?.dimensionScores || {})?.map(([key, dimension]) => (
            <div key={key} className="bg-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-primary">{dimension?.title}</h4>
                <span className="text-2xl font-bold text-secondary">{dimension?.score}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-secondary to-primary h-3 rounded-full transition-all duration-1000"
                  style={{ width: `${dimension?.score}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 mb-8">
          <h3 className="text-xl font-bold text-primary mb-4 flex items-center">
            <Icon name="Lightbulb" size={24} className="mr-2" />
            Recommended Next Steps
          </h3>
          <ul className="space-y-3">
            {result?.nextSteps?.map((step, index) => (
              <li key={index} className="flex items-start">
                <Icon name="CheckCircle" size={20} className="text-secondary mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{step}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Save Results */}
        <div className="bg-surface rounded-lg p-6">
          <h4 className="font-semibold text-primary mb-4">Save Your Results</h4>
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              placeholder="Enter your email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              className="flex-1"
            />
            <Button
              iconName="Save"
              iconPosition="left"
              onClick={handleSaveResults}
              disabled={saving}
              className="bg-secondary hover:bg-secondary/90"
            >
              {saving ? 'Saving...' : 'Save Results'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Save your results to track progress over time and receive personalized recommendations
          </p>
        </div>

        <div className="flex justify-center gap-4 mt-8">
          <Button
            variant="outline"
            onClick={() => {
              setShowResults(false);
              setCurrentStep(0);
              setAnswers({});
              setResult(null);
            }}
          >
            Retake Assessment
          </Button>
          <Button
            iconName="Calendar"
            iconPosition="left"
            onClick={() => navigate('/contact-multi-channel-engagement-hub')}
            className="bg-primary hover:bg-primary/90"
          >
            Book Consultation
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-primary">AI Readiness Assessment</h2>
          <span className="text-sm text-muted-foreground">
            Step {currentStep + 1} of {assessmentDimensions?.length}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div
            className="bg-gradient-to-r from-secondary to-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Current Dimension */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mr-4">
            <Icon name="Target" size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-primary">{currentDimension?.title}</h3>
            <p className="text-sm text-muted-foreground">{currentDimension?.description}</p>
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-6">
          {currentDimension?.questions?.map((question, qIndex) => (
            <div key={question?.id} className="bg-surface rounded-lg p-6">
              <h4 className="font-semibold text-primary mb-4">
                {qIndex + 1}. {question?.question}
              </h4>
              <div className="space-y-3">
                {question?.options?.map((option) => (
                  <label
                    key={option?.value}
                    className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      answers?.[question?.id] === option?.value
                        ? 'border-secondary bg-secondary/5' :'border-gray-200 hover:border-secondary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={question?.id}
                      value={option?.value}
                      checked={answers?.[question?.id] === option?.value}
                      onChange={() => handleAnswer(question?.id, option?.value)}
                      className="mt-1 mr-3 accent-secondary"
                    />
                    <span className="text-sm text-slate-700">{option?.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentStep === 0}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isCurrentStepComplete}
          iconName={currentStep === assessmentDimensions?.length - 1 ? 'CheckCircle' : 'ChevronRight'}
          iconPosition="right"
          className="bg-secondary hover:bg-secondary/90"
        >
          {currentStep === assessmentDimensions?.length - 1 ? 'View Results' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default AIReadinessAssessment;